import { Tag, Typography } from 'antd';
import {
  filterRegularEvaluations,
  totalRegularWeight
} from './evaluationWeightsUtils';
import { useMemo } from 'react';
import type { IProfessorAreaEvaluationsList } from 'interfaces/professor_area/evaluations';
import type { ReactNode } from 'react';

/**
 * Paleta marca — ordem otimizada para **vizinhos contrastarem** (ciclo fechado).
 * Evita pares de azuis/pastéis iguais lado a lado (#adc5e0 ficou de fora por ser quase igual a #aec6e0).
 */
const BAR_COLORS = [
  '#e72e52', // apoio — vermelho
  '#a2c0c8', // gradiente
  '#766aad', // apoio — roxo
  '#ec6190', // primário
  '#aec6e0', // apoio — azul claro
  '#343232', // neutro (segmento escuro)
  '#bbb6da' // gradiente — lavanda
];

export const FINAL_PREVIEW_ID = '__final_preview__';

interface EvaluationWeightsVisualizationProps {
  evaluations: IProfessorAreaEvaluationsList[];
  /** Destaca um segmento (ex.: prévia no modal de criação) */
  highlightSegmentId?: string;
  /** Substitui o texto introdutório padrão */
  introText?: ReactNode;
  /** Oculta a linha de texto acima da barra (só gráfico + legenda) */
  hideIntro?: boolean;
  /**
   * Quando preenchido, a barra é substituída por um único segmento 100%
   * representando a recuperação em prévia. Ignora `evaluations`.
   */
  finalPreview?: {
    name: string;
    evaluation_type?: 'Assessment' | 'Assignment';
  };
}

export const EvaluationWeightsVisualization = ({
  evaluations,
  highlightSegmentId,
  introText,
  hideIntro,
  finalPreview
}: EvaluationWeightsVisualizationProps) => {
  const isAssignmentFinal = finalPreview?.evaluation_type === 'Assignment';

  const segments = useMemo(() => {
    if (finalPreview) {
      const fallback =
        finalPreview.evaluation_type === 'Assignment'
          ? 'Atividade de recuperação'
          : 'Prova final';
      return [
        {
          id: FINAL_PREVIEW_ID,
          name: finalPreview.name.trim() || fallback,
          weight: 100,
          percent: 100,
          color: '#ec6190'
        }
      ];
    }

    const regular = filterRegularEvaluations(evaluations);
    const total = totalRegularWeight(evaluations);

    if (regular.length === 0 || total <= 0) {
      return [];
    }
    return regular.map((e, index) => {
      const w = Number(e.weight ?? 0);
      const pct = (w / total) * 100;
      return {
        id: e.id,
        name: e.name,
        weight: w,
        percent: pct,
        color: BAR_COLORS[index % BAR_COLORS.length]
      };
    });
  }, [evaluations, finalPreview]);

  const effectiveHighlight = finalPreview
    ? FINAL_PREVIEW_ID
    : highlightSegmentId;
  const effectiveIntro = finalPreview ? (
    isAssignmentFinal ? (
      <>
        A <strong>Atividade de recuperação</strong> substitui a média ponderada
        das atividades padrão: quando realizada, vale 100% da nota final do
        aluno.
      </>
    ) : (
      <>
        A <strong>Prova final</strong> substitui a média ponderada das
        avaliações padrão: quando realizada, vale 100% da nota final do aluno.
      </>
    )
  ) : (
    introText
  );

  if (segments.length === 0) {
    const regular = filterRegularEvaluations(evaluations);

    if (regular.length === 0) {
      return null;
    }
    return (
      <div
        className="rounded border px-3 py-2 text-sm"
        style={{
          borderColor: '#adc5e0',
          backgroundColor: '#f5f5f5',
          color: '#343232'
        }}
      >
        <Typography.Text style={{ color: '#343232' }}>
          Os pesos estão todos em zero: nenhuma avaliação padrão entra na média
          ponderada. Use pesos maiores que zero ou trate essas avaliações como
          pesquisa (fora da média).
        </Typography.Text>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {!hideIntro && (
        <Typography.Text className="text-sm" style={{ color: '#343232' }}>
          {effectiveIntro ?? (
            <>
              Participação de cada avaliação <strong>padrão</strong> na média
              ponderada (largura proporcional ao peso):
            </>
          )}
        </Typography.Text>
      )}
      <div
        className="flex h-4 w-full gap-1 overflow-hidden rounded-full p-0.5"
        style={{ backgroundColor: '#f5f5f5' }}
        role="img"
        aria-label="Distribuição visual dos pesos na média ponderada"
      >
        {segments.map(s => (
          <div
            key={s.id}
            title={`${s.name}: peso ${s.weight} (${s.percent.toFixed(1)}%)`}
            className={`min-w-[2px] rounded-sm ${
              effectiveHighlight === s.id ? 'relative z-[1]' : ''
            }`}
            style={{
              flex: `${s.percent} 1 0%`,
              backgroundColor: s.color,
              ...(effectiveHighlight === s.id
                ? { boxShadow: 'inset 0 0 0 2px #ec6190' }
                : {})
            }}
          />
        ))}
      </div>
      <ul className="m-0 grid list-none gap-x-4 gap-y-1 p-0 sm:grid-cols-2">
        {segments.map(s => (
          <li key={s.id} className="flex items-start gap-2 text-sm">
            <span
              className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: s.color }}
            />
            <span>
              <span className="font-medium" style={{ color: '#343232' }}>
                {s.name}
              </span>
              {effectiveHighlight === s.id && (
                <Tag
                  className="ml-1 align-middle border-0 text-xs text-white"
                  style={{ backgroundColor: '#ec6190' }}
                >
                  Nova
                </Tag>
              )}
              <span style={{ color: '#343232', opacity: 0.65 }}>
                {' '}
                {s.id === FINAL_PREVIEW_ID
                  ? '— 100% da nota'
                  : `— peso ${s.weight} (${s.percent.toFixed(0)}%)`}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
